import type Plan from '~/types/Plan';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { type UserPlan } from '~/types/Plan';
import { getFailed, getNotRequested, getRequesting, getSuccess } from '~/utils/fetchable';

export type PlansState = {
  adjustedPlan: Plan | null;
  closeAccountError: Maybe<string>;
  closeAccountModalIsOpen: Readonly<boolean>;
  currentPlan: Readonly<Maybe<UserPlan>>;
  fetchPlanRequest: Requestable<UserPlan, string>;
  fetchPricingPlansRequest: Requestable<Plan[], string>;
  hasClosedWixProPlanNotification: Readonly<boolean>;
  nextPlan: Readonly<Maybe<UserPlan>>;
  setPlanRequest: Requestable<Maybe<UserPlan>, string>;
  setPlanRequestSource: 'downgrade' | 'upgrade' | null;
};

export const initialState: PlansState = {
  adjustedPlan: null,
  closeAccountError: null,
  closeAccountModalIsOpen: false,
  currentPlan: null,
  fetchPlanRequest: getNotRequested(),
  fetchPricingPlansRequest: getNotRequested(),
  hasClosedWixProPlanNotification: false,
  nextPlan: null,
  setPlanRequest: getNotRequested(),
  setPlanRequestSource: null,
};

const plansReducer = (state: PlansState = initialState, action: ActionTypes): PlansState => {
  switch (action.type) {
    case ActionKeys.FETCH_USER_PLANS:
      return {
        ...state,
        fetchPlanRequest: getRequesting(),
      };
    case ActionKeys.FETCH_USER_PLANS_SUCCESS:
      const now = Date();
      // The backend only returns the current plan and the next plan if any
      const currentPlan = action.payload.userPlans.filter(
        (plan) =>
          plan.startDate.isSameOrBefore(now) && (!plan.endDate || plan.endDate.isAfter(now)),
      );
      const nextPlan = action.payload.userPlans.filter((plan) => plan.startDate.isAfter(now));

      return {
        ...state,
        currentPlan: currentPlan[0] || null,
        fetchPlanRequest: getSuccess(currentPlan[0]),
        nextPlan: nextPlan[0] || null,
      };
    case ActionKeys.FETCH_USER_PLANS_ERROR:
      return {
        ...state,
        fetchPlanRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.FETCH_PRICING_PLANS:
      return {
        ...state,
        fetchPricingPlansRequest: getRequesting(),
      };
    case ActionKeys.FETCH_PRICING_PLANS_SUCCESS:
      return {
        ...state,
        fetchPricingPlansRequest: getSuccess(action.payload.pricingPlans),
      };
    case ActionKeys.FETCH_PRICING_PLANS_ERROR:
      return {
        ...state,
        fetchPricingPlansRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.UPDATE_USER_PLAN:
      return {
        ...state,
        setPlanRequest: getRequesting(),
      };
    case ActionKeys.UPDATE_USER_PLAN_ERROR:
      return {
        ...state,
        setPlanRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.UPDATE_USER_PLAN_SUCCESS:
      return {
        ...state,
        setPlanRequest: getSuccess(action.payload.userPlan),
        setPlanRequestSource: action.payload.source ?? null,
      };
    case ActionKeys.RESET_USER_PLAN_REQUEST:
      return {
        ...state,
        setPlanRequest: getNotRequested(),
        setPlanRequestSource: null,
      };
    case ActionKeys.CLOSE_WIX_PRO_PLAN_NOTIFICATION:
      return {
        ...state,
        hasClosedWixProPlanNotification: true,
      };
    case ActionKeys.OPEN_CLOSE_ACCOUNT_MODAL:
      return {
        ...state,
        closeAccountModalIsOpen: true,
      };
    case ActionKeys.DISMISS_CLOSE_ACCOUNT_MODAL:
      return {
        ...state,
        closeAccountError: null,
        closeAccountModalIsOpen: false,
      };
    case ActionKeys.SUBMIT_CLOSE_ACCOUNT_FORM:
    case ActionKeys.CLOSE_ACCOUNT_SUCCESS:
      return {
        ...state,
        closeAccountError: null,
      };
    case ActionKeys.CLOSE_ACCOUNT_ERROR:
      return {
        ...state,
        closeAccountError: action.payload.errorMessage,
      };
    case ActionKeys.SET_ADJUSTED_PLAN:
      return {
        ...state,
        adjustedPlan: action.payload.plan,
      };
    default:
      return state;
  }
};

export default plansReducer;
